import React, { useState, useEffect } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  NavLink,
  useLocation,
} from 'react-router-dom';
import './App.css';
import Dashboard from './components/Dashboard';
import Portfolio from './components/Portfolio';
import Analysis from './components/Analysis';
import ReportDetailRoute from './components/ReportDetailRoute';
import Messages from './components/Messages';
import ChatBot from './components/ChatBot';
import Settings from './components/Settings';
import TranscriptUpload from './components/TranscriptUpload';
import TranscriptDetailRoute from './components/TranscriptDetailRoute';
import Mentions from './components/Mentions';
import MentionDetail from './components/MentionDetail';
import Philosophy from './components/Philosophy';
import Securities from './components/Securities';
import Scorecard from './components/Scorecard';
import MarketRegime from './components/MarketRegime';
import SimpleAuthButton from './components/SimpleAuthButton';
import LoginForm from './components/LoginForm';
import { useAuth } from './contexts/AuthContext';
import {
  LayoutDashboard, Briefcase, Brain, MessageSquare, Bot,
  Settings as SettingsIcon, FileText, Tag, Menu, X, BookOpen, Link2, Trophy, Gauge,
} from 'lucide-react';

const NAV_ITEMS = [
  { to: '/dashboard',   label: 'Dashboard',   icon: LayoutDashboard },
  { to: '/market-regime', label: 'Market Regime', icon: Gauge },
  { to: '/portfolio',   label: 'Portfolio',   icon: Briefcase },
  { to: '/analysis',    label: 'AI Analysis', icon: Brain },
  { to: '/mentions',    label: 'Tickers & Themes', icon: Tag },
  { to: '/scorecard',   label: 'Scorecard',   icon: Trophy },
  { to: '/messages',    label: 'Messages',    icon: MessageSquare },
  { to: '/chat',        label: 'AI Chat',     icon: Bot },
  { to: '/transcripts', label: 'Transcripts', icon: FileText },
  { to: '/philosophy',  label: 'Philosophy',  icon: BookOpen },
  { to: '/securities',  label: 'Securities',  icon: Link2 },
  { to: '/settings',    label: 'Settings',    icon: SettingsIcon },
];

/** Closes the mobile sidebar whenever the user navigates. */
const useCloseSidebarOnNavigate = (close) => {
  const location = useLocation();
  useEffect(() => {
    if (window.innerWidth <= 768) close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
};

const Shell = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  useCloseSidebarOnNavigate(() => setSidebarOpen(false));

  // Close sidebar when window resizes past mobile breakpoint.
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="App">
      {/* Mobile header with hamburger menu */}
      <header className="mobile-header">
        <button
          className="hamburger-btn"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label="Toggle navigation"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <h1 className="mobile-title">RussAdvisor</h1>
      </header>

      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      <nav className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <h1>RussAdvisor</h1>
          <p className="subtitle">AI Investment Assistant</p>
        </div>

        <div className="nav-items">
          {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
            >
              <Icon size={20} />
              <span>{label}</span>
            </NavLink>
          ))}
        </div>

        <div className="sidebar-footer">
          <SimpleAuthButton />
          <p className="version">v1.0.0</p>
        </div>
      </nav>

      <main className="main-content">
        <Routes>
          <Route path="/"                     element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard"            element={<Dashboard />} />
          <Route path="/portfolio"            element={<Portfolio />} />
          <Route path="/analysis"                  element={<Analysis />} />
          <Route path="/analysis/:reportId"        element={<ReportDetailRoute />} />
          <Route path="/mentions"                  element={<Mentions />} />
          <Route path="/mentions/:kind"            element={<Mentions />} />
          <Route path="/mentions/:kind/:key"       element={<MentionDetail />} />
          <Route path="/scorecard"            element={<Scorecard />} />
          <Route path="/market-regime"        element={<MarketRegime />} />
          <Route path="/messages"             element={<Messages />} />
          <Route path="/chat"                       element={<ChatBot />} />
          <Route path="/chat/:conversationId"       element={<ChatBot />} />
          <Route path="/transcripts"                  element={<TranscriptUpload />} />
          <Route path="/transcripts/:transcriptId"    element={<TranscriptDetailRoute />} />
          <Route path="/philosophy"           element={<Philosophy />} />
          <Route path="/securities"           element={<Securities />} />
          <Route path="/settings"             element={<Settings />} />
          <Route path="*"                     element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
    </div>
  );
};

function App() {
  const { isAuthenticated, loading, login } = useAuth();

  if (loading) {
    return <div className="loading">Initializing...</div>;
  }

  if (!isAuthenticated) {
    return <LoginForm onLogin={login} loading={loading} />;
  }

  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}

export default App;
